import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import createDecorator from 'final-form-calculate';
import { FormControl, InputGroup } from 'react-bootstrap';
import currencyConversion from '../../utils/currencyConversion';

import './ScheduleAB.less';

const MoneyField = ({ input, ...rest }) => (
  <InputGroup>
    <InputGroup.Addon>$</InputGroup.Addon>
    <FormControl bsSize="small" type="text" {...input} {...rest} />
  </InputGroup>
);

class ScheduleABForm extends Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    schedule: PropTypes.any.isRequired,
    isDraft: PropTypes.bool,
  };

  static defaultProps = {
    onSubmit: () => null,
  };

  render() {
    const {
      onSubmit,
      schedule: { data },
    } = this.props;
    const calculator = createDecorator(
      {
        field: [
          'cash',
          'deposits',
          'acct_rcpt',
          'investments',
          'inventory',
          'farming',
          'office',
          'machinery',
          'intangibles',
          'others',
        ],
        updates: {
          personal_total: (fieldValue, allValues) =>
            currencyConversion.toDollars(
              currencyConversion.toCents(allValues.cash) +
                currencyConversion.toCents(allValues.deposits) +
                currencyConversion.toCents(allValues.acct_rcpt) +
                currencyConversion.toCents(allValues.investments) +
                currencyConversion.toCents(allValues.inventory) +
                currencyConversion.toCents(allValues.farming) +
                currencyConversion.toCents(allValues.office) +
                currencyConversion.toCents(allValues.machinery) +
                currencyConversion.toCents(allValues.intangibles) +
                currencyConversion.toCents(allValues.others)
            ),
        },
      },
      {
        field: 'property',
        updates: {
          real_total: (property) => currencyConversion.toDollars(currencyConversion.toCents(property)),
        },
      },
      {
        field: ['personal_total', 'real_total'],
        updates: {
          total: (ignoredValue, allValues) =>
            currencyConversion.toDollars(
              currencyConversion.toCents(allValues.personal_total) + currencyConversion.toCents(allValues.real_total)
            ),
        },
      }
    );

    const field_format = (value) =>
      `$${(value || 0)
        .toFixed(2)
        .toString()
        .replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
    const initialData = {
      acct_rcpt: currencyConversion.toDollarsOrStr(data.acct_rcpt),
      cash: currencyConversion.toDollarsOrStr(data.cash),
      deposits: currencyConversion.toDollarsOrStr(data.deposits),
      farming: currencyConversion.toDollarsOrStr(data.farming),
      intangibles: currencyConversion.toDollarsOrStr(data.intangibles),
      inventory: currencyConversion.toDollarsOrStr(data.inventory),
      investments: currencyConversion.toDollarsOrStr(data.investments),
      machinery: currencyConversion.toDollarsOrStr(data.machinery),
      office: currencyConversion.toDollarsOrStr(data.office),
      others: currencyConversion.toDollarsOrStr(data.others),
      personal_total: currencyConversion.toDollarsOrStr(data.personal_total),
      property: currencyConversion.toDollarsOrStr(data.property),
      real_total: currencyConversion.toDollarsOrStr(data.real_total),
      total: currencyConversion.toDollarsOrStr(data.total),
    };

    return (
      <Form
        initialValues={initialData}
        onSubmit={onSubmit}
        decorators={[calculator]}
        render={({ handleSubmit, submitting, pristine }) => (
          <form onSubmit={handleSubmit} id="parser-schedule-ab-form">
            <table className="sticky-enabled tablesorter">
              <thead>
                <tr>
                  <th>Type of property</th>
                  <th>Current value of personal&nbsp;property</th>
                  <th>Current value of real&nbsp;property</th>
                </tr>
              </thead>
              <tbody>
                <tr className="odd">
                  <td>
                    <label htmlFor="cash">
                      <span className="label">Cash, cash equivalents, and financial assets.</span>
                      <span className="schedule-tips">Copy line 5, Part 1</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="cash" component={MoneyField} id="cash" size={20} />
                  </td>
                  <td />
                </tr>
                <tr className="even">
                  <td>
                    <label htmlFor="deposits">
                      <span className="label">Deposits and prepayments.</span>
                      <span className="schedule-tips">Copy line 9, Part 2</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="deposits" component={MoneyField} id="deposits" size={20} />
                  </td>
                  <td />
                </tr>
                <tr className="odd">
                  <td>
                    <label htmlFor="acct-rcpt">
                      <span className="label">Accounts receivable</span>
                      <span className="schedule-tips">Copy line 12, Part 3</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="acct_rcpt" component={MoneyField} id="acct-rcpt" size={20} />
                  </td>
                  <td />
                </tr>
                <tr className="even">
                  <td>
                    <label htmlFor="investments">
                      <span className="label">Investments.</span>
                      <span className="schedule-tips">Copy line 17, Part 4</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="investments" component={MoneyField} id="investments" size={20} />
                  </td>
                  <td />
                </tr>
                <tr className="odd">
                  <td>
                    <label htmlFor="inventory">
                      <span className="label">Inventory.</span>
                      <span className="schedule-tips">Copy line 23, Part 5</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="inventory" component={MoneyField} id="inventory" size={20} />
                  </td>
                  <td />
                </tr>
                <tr className="even">
                  <td>
                    <label htmlFor="farming">
                      <span className="label">Farming and fishing-related assets.</span>
                      <span className="schedule-tips">Copy line 33, Part 6</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="farming" component={MoneyField} id="farming" size={20} />
                  </td>
                  <td />
                </tr>
                <tr className="odd">
                  <td>
                    <label htmlFor="office">
                      <span className="label">Office furniture, fixtures, and equipment; and collectibles.</span>
                      <span className="schedule-tips">Copy line 43, Part 7</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="office" component={MoneyField} id="office" size={20} />
                  </td>
                  <td />
                </tr>
                <tr className="even">
                  <td>
                    <label htmlFor="machinery">
                      <span className="label">Machinery, equipment, and vehicles.</span>
                      <span className="schedule-tips">Copy line 51, Part 8</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="machinery" component={MoneyField} id="machinery" size={20} />
                  </td>
                  <td />
                </tr>
                <tr className="odd">
                  <td>
                    <label htmlFor="property">
                      <span className="label">Real Property.</span>
                      <span className="schedule-tips">Copy line 56, Part 9</span>
                    </label>
                  </td>
                  <td />
                  <td className="money">
                    <Field name="property" component={MoneyField} id="property" size={20} />
                  </td>
                </tr>
                <tr className="even">
                  <td>
                    <label htmlFor="intangibles">
                      <span className="label">Intangibles and intellectual property.</span>
                      <span className="schedule-tips">Copy line 66, Part 10</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="intangibles" component={MoneyField} id="intangibles" size={20} />
                  </td>
                  <td />
                </tr>
                <tr className="odd">
                  <td>
                    <label htmlFor="others">
                      <span className="label">All other assets.</span>
                      <span className="schedule-tips">Copy line 78, Part 11</span>
                    </label>
                  </td>
                  <td className="money">
                    <Field name="others" component={MoneyField} id="others" size={20} />
                  </td>
                  <td />
                </tr>
              </tbody>
              <tfoot>
                <tr className="even">
                  <td>
                    <span className="label">Total</span>
                  </td>
                  <td className="money subtotal" id="personal">
                    <Field
                      name="personal_total"
                      component={MoneyField}
                      format={(value) => field_format(value)}
                      id="totals"
                      type="text"
                      disabled
                    />
                  </td>
                  <td className="money subtotal" id="real">
                    <Field
                      name="real_total"
                      component={MoneyField}
                      format={(value) => field_format(value)}
                      id="totals"
                      type="text"
                      disabled
                    />
                  </td>
                </tr>
                <tr className="odd">
                  <td>
                    <span className="label">Total of all property on Schedule A/B.</span>
                  </td>
                  <td />
                  <td className="money total" id="total">
                    <Field
                      name="total"
                      component={MoneyField}
                      format={(value) => field_format(value)}
                      id="totals"
                      type="text"
                      disabled
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
            <button
              type="submit"
              value="Submit"
              className="form-submit"
              disabled={submitting || (pristine && !this.props.isDraft)}
            >
              Save
            </button>
            <a id="alfresco-clear" className="cancel-link" onClick={this.props.onCancel}>
              Cancel
            </a>
            <div id="schedule-ab-validation-messages" className="warning" style={{ display: 'none' }} />
          </form>
        )}
      />
    );
  }
}

export default ScheduleABForm;



// WEBPACK FOOTER //
// ./src/scheduleform/schedule_ab/ScheduleABForm.jsx